// Where a <video> or <audio> gets a file from. A large (chunked) file streams: through the
// rhema-media:// protocol in the desktop app, and through the service worker on the web. Small
// files, and platforms that cannot stream, download and decrypt the whole file first, as before.
//
// - Desktop (Tauri, macOS WebKit included): streams.
// - Web browsers with the service worker running (production builds): stream.
// - Android app: streams only if the webview runs the service worker for the app's origin.
//   Where it does not, the whole file downloads first.
// - iOS app: WKWebView has no service workers for the app's origin, so the whole file downloads
//   first. A native protocol handler, like the desktop one, would lift this.

import type { MediaRef } from "@rhema/client";
import { isChunked } from "@rhema/client/chunked";
import { detectPlatform } from "../platform";
import { LOOKUP, REGISTER, type StreamEntry, streamId, UNREGISTER } from "./streamServer";

export type Playable = {
  url: string;
  /** "stream" when it plays while downloading, "blob" when the whole file was fetched first. */
  mode: "stream" | "blob";
  /** Lets go of the stream or the object URL. Call when the player closes. */
  release(): void;
};

export type PlaybackDeps = {
  /**
   * A fresh presigned download URL for a chunked file's storage key, such as
   * `(await api.mediaUrl(key, { chunked: true })).url`.
   */
  mediaUrl(key: string): Promise<string>;
  /** The whole file, decrypted. */
  download(ref: MediaRef): Promise<Blob>;
  kind?: "web" | "tauri" | "capacitor";
  serviceWorker?: ServiceWorkerContainer | null;
  tauri?: TauriStreamApi;
};

/** The desktop commands in media_stream.rs. */
export type TauriStreamApi = {
  invoke<T>(cmd: string, args: Record<string, unknown>): Promise<T>;
  convertFileSrc(path: string, protocol: string): string;
};

/** Presigned URLs last an hour. The desktop stream gets a new one well before that. */
const REFRESH_MS = 40 * 60 * 1000;

function entryOf(ref: MediaRef, url: string): StreamEntry {
  return {
    key: ref.key,
    url,
    file_key: ref.file_key,
    iv: ref.iv,
    size: ref.size,
    chunk_size: ref.chunk_size ?? 0,
    mime: ref.mime,
    sha256: ref.sha256,
    version: ref.version ?? 1,
  };
}

// Entries this page has registered, so it can tell a restarted worker about them again.
const pageEntries = new Map<string, StreamEntry>();
let answering = false;

function answerLookups(sw: ServiceWorkerContainer): void {
  if (answering) return;
  answering = true;
  sw.addEventListener("message", (event) => {
    const data = event.data as { type?: string; id?: string } | null;
    if (data?.type !== LOOKUP || !data.id) return;
    event.ports[0]?.postMessage({ entry: pageEntries.get(data.id) ?? null });
  });
}

async function viaWorker(
  ref: MediaRef,
  deps: PlaybackDeps,
  sw: ServiceWorkerContainer,
): Promise<Playable | null> {
  const controller = sw.controller;
  if (!controller) return null;
  answerLookups(sw);
  const id = streamId();
  const entry = entryOf(ref, await deps.mediaUrl(ref.key));
  pageEntries.set(id, entry);
  const channel = new MessageChannel();
  const ok = await new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), 1500);
    channel.port1.onmessage = () => {
      clearTimeout(timer);
      resolve(true);
    };
    controller.postMessage({ type: REGISTER, id, entry }, [channel.port2]);
  });
  if (!ok) {
    pageEntries.delete(id);
    return null;
  }
  return {
    url: `/__media/${id}`,
    mode: "stream",
    release: () => {
      pageEntries.delete(id);
      sw.controller?.postMessage({ type: UNREGISTER, id });
    },
  };
}

async function viaDesktop(ref: MediaRef, deps: PlaybackDeps, tauri: TauriStreamApi): Promise<Playable> {
  const url = await deps.mediaUrl(ref.key);
  const id = await tauri.invoke<string>("media_stream_open", {
    args: {
      url,
      fileKey: ref.file_key,
      iv: ref.iv,
      size: ref.size,
      chunkSize: ref.chunk_size,
      mime: ref.mime,
    },
  });
  const timer = setInterval(() => {
    void deps
      .mediaUrl(ref.key)
      .then((fresh) => tauri.invoke("media_stream_update", { id, url: fresh }))
      .catch(() => undefined);
  }, REFRESH_MS);
  return {
    url: tauri.convertFileSrc(id, "rhema-media"),
    mode: "stream",
    release: () => {
      clearInterval(timer);
      void tauri.invoke("media_stream_close", { id }).catch(() => undefined);
    },
  };
}

async function viaBlob(ref: MediaRef, deps: PlaybackDeps): Promise<Playable> {
  const url = URL.createObjectURL(await deps.download(ref));
  return { url, mode: "blob", release: () => URL.revokeObjectURL(url) };
}

/** A URL a media element can play this file from. */
export async function playableUrl(ref: MediaRef, deps: PlaybackDeps): Promise<Playable> {
  if (!isChunked(ref)) return viaBlob(ref, deps);
  const kind = deps.kind ?? detectPlatform();
  try {
    if (kind === "tauri") {
      const tauri = deps.tauri ?? ((await import("@tauri-apps/api/core")) as TauriStreamApi);
      return await viaDesktop(ref, deps, tauri);
    }
    const sw =
      deps.serviceWorker !== undefined
        ? deps.serviceWorker
        : typeof navigator !== "undefined"
          ? navigator.serviceWorker
          : null;
    if (sw) {
      const streamed = await viaWorker(ref, deps, sw);
      if (streamed) return streamed;
    }
  } catch {
    // Streaming is an improvement. When it cannot start, the file downloads as before.
  }
  return viaBlob(ref, deps);
}
